import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Upload, X, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function LegacyOrderImportModal({ onClose }) {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleRun = async () => {
    if (!file) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const upload = await base44.integrations.Core.UploadFile({ file });
      const res = await base44.functions.invoke('importLegacyOrders', { file_url: upload.file_url });
      setResult(res.data);
      qc.invalidateQueries({ queryKey: ['admin-orders'] });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#111115] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest mb-1">DATA MIGRATION</p>
            <h3 className="text-white font-black text-sm uppercase">Import Legacy Orders</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-white/40 text-xs leading-relaxed mb-4">
          Upload a CSV order export from your previous system (ShopWired / Square Online / eBay). Each line item becomes
          its own order record, linked to a current product by SKU where possible. Re-importing the same file skips
          duplicates automatically.
        </p>

        <label className="block border border-dashed border-white/15 hover:border-primary/40 transition-colors p-6 text-center cursor-pointer mb-4">
          <input
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(''); }}
          />
          {file ? (
            <div className="flex items-center justify-center gap-2 text-white text-sm">
              <CheckCircle2 className="w-4 h-4 text-primary" /> {file.name}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-white/40">
              <Upload className="w-6 h-6" />
              <span className="text-xs font-bold tracking-wider">CHOOSE A CSV FILE</span>
            </div>
          )}
        </label>

        {error && (
          <div className="flex items-start gap-2 border border-red-500/30 bg-red-500/5 p-3 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <p className="text-red-300 text-xs">{error}</p>
          </div>
        )}

        {result && (
          <div className="border border-primary/30 bg-primary/5 p-4 mb-4 space-y-1.5">
            <p className="text-white font-bold text-sm">Import complete</p>
            <p className="text-white/50 text-xs">Imported: <span className="text-white font-bold">{result.imported}</span></p>
            <p className="text-white/50 text-xs">Skipped duplicates: <span className="text-white font-bold">{result.skipped_duplicates}</span></p>
            <p className="text-white/50 text-xs">Products matched: <span className="text-white font-bold">{result.products_matched}</span></p>
            <p className="text-white/50 text-xs">Total rows read: <span className="text-white font-bold">{result.total_rows}</span></p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/5">
          <button onClick={onClose} className="text-white/50 hover:text-white text-xs font-bold tracking-wider px-4 py-2.5">
            {result ? 'CLOSE' : 'CANCEL'}
          </button>
          <button
            onClick={handleRun}
            disabled={!file || busy}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-2.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? 'IMPORTING…' : 'IMPORT ORDERS'}
          </button>
        </div>
      </div>
    </div>
  );
}